// ─────────────────────────────────────────────────────────────────────────
// useJobSheetStorage.ts
// เดิมอ่าน/เขียน localStorage['rs_jobs'] ตรง ๆ — ตอนนี้คุยกับ
// api/job_public.php (ไม่ต้อง login เหมือนเดิม เพราะลิงก์คนขับไม่มีระบบ
// auth) แต่กุญแจเข้าถึงใน URL เปลี่ยนจาก job id (เดา/ไล่เลขได้ง่าย) เป็น
// driver_token แบบสุ่ม (?token=...) แทน — ดู generate_driver_token() ใน
// helpers.php และคำอธิบายใน job_public.php
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { jobPublicApi } from './api';
import type { JobSheetJob, JobSheetUpdate } from './jobSheet.types';

/** อ่านค่า `?token=XXX` จาก URL (แทนที่ `?job=ID` เดิม — ดูหมายเหตุด้านบน) */
function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

export interface UseJobSheetStorageResult {
  job: JobSheetJob | null;
  /** True เมื่อโหลดข้อมูลครั้งแรกจากเซิร์ฟเวอร์เสร็จแล้ว (job อาจยังเป็น null ได้ถ้าไม่พบ) */
  isLoaded: boolean;
  /** ส่งค่าที่เปลี่ยนแปลงไปอัปเดตที่เซิร์ฟเวอร์ (PATCH) แล้วอัปเดต state ทันที (optimistic) */
  updateJob: (fields: JobSheetUpdate) => void;
  /** โหลดใบงานล่าสุดจากเซิร์ฟเวอร์ซ้ำอีกครั้ง (ใช้ตอน poll รอแอดมินอนุมัติสลิป) */
  refetchJob: () => JobSheetJob | null;
}

export function useJobSheetStorage(): UseJobSheetStorageResult {
  const [job, setJob] = useState<JobSheetJob | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // เก็บ job ล่าสุดไว้ใน ref ด้วย เพื่อให้ refetchJob() แบบ sync-return ยังใช้ได้
  const jobRef = useRef<JobSheetJob | null>(null);
  // token คือ "กุญแจเข้าถึง" จริง — เก็บแยกจาก job.id เสมอ เพราะ id ของ
  // ใบงานไม่ใช่สิ่งที่ใช้เรียก job_public.php ได้อีกต่อไป
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    const token = getTokenFromUrl();
    if (!token) {
      setIsLoaded(true);
      return;
    }
    tokenRef.current = token;
    jobPublicApi
      .get<JobSheetJob>(token)
      .then((found) => {
        jobRef.current = found;
        setJob(found);
      })
      .catch(() => {
        // ไม่พบใบงาน หรือเซิร์ฟเวอร์ไม่ตอบสนอง — job จะยังเป็น null
      })
      .finally(() => setIsLoaded(true));
  }, []);

  const updateJob = useCallback((fields: JobSheetUpdate) => {
    setJob((current) => {
      if (!current) return current;
      const optimistic: JobSheetJob = { ...current, ...fields };
      jobRef.current = optimistic;
      return optimistic;
    });
    const token = tokenRef.current;
    if (!token) return;
    jobPublicApi.update<JobSheetJob>(token, fields).then((fresh) => {
      jobRef.current = fresh;
      setJob(fresh);
    }).catch(() => {
      // เครือข่ายขัดข้อง — ค่า optimistic ที่แสดงอยู่ยังคงอยู่ในจอ ผู้ใช้กดซ้ำได้
    });
  }, []);

  // คงชื่อ/รูปแบบ synchronous เดิมไว้ (คืนค่าที่มีอยู่ในมือทันที) แต่ยิง
  // fetch ใหม่ทิ้งไว้เบื้องหลังเพื่ออัปเดต state ให้สดที่สุดสำหรับรอบถัดไป
  const refetchJob = useCallback((): JobSheetJob | null => {
    const token = tokenRef.current;
    if (!token) return jobRef.current;
    jobPublicApi
      .get<JobSheetJob>(token)
      .then((fresh) => {
        jobRef.current = fresh;
        setJob(fresh);
      })
      .catch(() => {});
    return jobRef.current;
  }, []);

  return { job, isLoaded, updateJob, refetchJob };
}
